// PDP entry to the existing apex quiz. No form, vendor, event or discount writer.
// Use the exact asset_url (including Shopify's version) used by the region.
// A bare relative import would instantiate a second registry with no roots.
const sealedScript = [...document.scripts].find(script => /\/dg-sealed\.js(?:\?|$)/.test(script.src));
const { sealedRoots } = sealedScript ? await import(sealedScript.src) : { sealedRoots: () => [] };
import { sharedWelcome, shareWelcome, watchWelcomeConsent } from './dg-welcome-state.js';

const SUPPRESS = 'dg-welcome-suppressed-until';
const SIGNED = 'dg-welcome-signed-up';
const ENTRY = 'dg-session-entry';
const DAY = 86400000;
const delayMs = 5000;
const scrollViewports = 1;
watchWelcomeConsent();
let entry = false;
try {
  // Claim on every theme page, even when no PDP dialog renders.
  entry = sessionStorage.getItem(ENTRY) === null;
  sessionStorage.setItem(ENTRY, '1');
  localStorage.getItem(SUPPRESS);
} catch { entry = false; }
const root = sealedRoots('welcome')[0];
const dialog = root?.querySelector('dialog[data-dg-welcome]');

function suppressed() {
  try { return !!sharedWelcome() || Number(localStorage.getItem(SUPPRESS)) > Date.now(); }
  catch { return true; }
}
function suppress() {
  const until = Date.now() + 30 * DAY;
  try { localStorage.setItem(SUPPRESS, String(until)); } catch { /* quiet */ }
  shareWelcome('dismissed', Math.floor(until / 1000));
}
function signedUp() {
  if (dialog?.hasAttribute('data-dg-known-subscriber') || sharedWelcome()?.s === 'submitted') return true;
  try { return localStorage.getItem(SIGNED) === '1'; } catch { return false; }
}

if (dialog) {
  let timer;
  let cancelled = false;
  let previousFocus;
  let leaving = false;
  let vendorOpen = false;
  const scopes = [document, ...sealedRoots()];
  const disarm = () => { clearTimeout(timer); window.removeEventListener('scroll', onScroll); };
  const stop = () => { cancelled = true; disarm(); };
  const visible = element => {
    const box = element.getBoundingClientRect();
    return element.checkVisibility({ opacityProperty: true, visibilityProperty: true })
      && box.bottom > 0 && box.right > 0 && box.top < innerHeight && box.left < innerWidth;
  };
  const busy = () => vendorOpen || scopes.some(scope => [...scope.querySelectorAll(
    'dialog[open]:not([data-dg-welcome]), [aria-modal="true"], details[open].menu-opening, .klaviyo-form, .klaviyo-form-overlay')].some(visible));
  const fire = () => {
    disarm();
    if (cancelled || document.visibilityState !== 'visible' || dialog.open || suppressed() || busy()) return;
    previousFocus = document.activeElement;
    dialog.toggleAttribute('data-dg-signed-up', signedUp());
    dialog.showModal();
    dialog.focus({ preventScroll: true });
  };
  const onScroll = () => { if (scrollY >= innerHeight * scrollViewports) fire(); };
  dialog.addEventListener('cancel', suppress);
  dialog.addEventListener('submit', suppress);
  dialog.addEventListener('close', () => {
    if (!leaving) suppress();
    leaving = false;
    if (previousFocus?.isConnected && previousFocus !== document.body) previousFocus.focus({ preventScroll: true });
  });
  let downOutside = false;
  const outside = e => {
    const box = dialog.getBoundingClientRect();
    return e.target === dialog && (e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom);
  };
  dialog.addEventListener('pointerdown', e => { downOutside = outside(e); });
  dialog.addEventListener('click', e => {
    const down = downOutside; downOutside = false;
    if (down && outside(e)) { suppress(); dialog.close(); }
  });
  const leave = e => {
    if (e.type === 'auxclick' && e.button !== 1) return;
    const link = e.target.closest?.('[data-dg-welcome-leave]');
    if (!link) return;
    suppress();
    // Same consent rule and allowlist as apex first-touch. Never copy click IDs,
    // product URLs, answers, return URLs, utm_content or utm_term.
    try {
      const consent = window.getCkyConsent?.();
      const url = new URL(link.href);
      for (const key of ['utm_source', 'utm_medium', 'utm_campaign']) {
        url.searchParams.delete(key);
        if (consent?.activeLaw !== 'ccpa' && !(consent?.activeLaw === 'gdpr' && consent.categories?.analytics === true)) continue;
        const value = (new URL(location.href).searchParams.get(key) ?? '').replace(/[^A-Za-z0-9_.-]/g, '').slice(0, 100);
        if (value) url.searchParams.set(key, value);
      }
      link.href = url.href;
    } catch { /* Plain quiz destination remains usable. */ }
  };
  dialog.addEventListener('click', leave);
  dialog.addEventListener('auxclick', leave);
  // A purchase action wins even when it happens before the delay expires.
  const purchase = e => {
    if (e.target.closest?.('form[action*="/cart"], [href*="/checkout"], [href$="/cart"], [data-dg-cart-link], .shopify-payment-button')) stop();
  };
  for (const scope of scopes) {
    scope.addEventListener('pointerdown', purchase, true);
    scope.addEventListener('click', purchase, true);
    scope.addEventListener('submit', purchase, true);
  }
  document.addEventListener('dg:added', stop);
  window.addEventListener('klaviyoForms', e => {
    if (e.detail?.type === 'open' || e.detail?.type === 'embedOpen') {
      vendorOpen = true; stop();
      if (dialog.open) dialog.close();
    }
    if (e.detail?.type === 'submit') {
      try { localStorage.setItem(SIGNED, '1'); } catch { /* quiet */ }
      shareWelcome('submitted', Math.floor(Date.now() / 1000) + 30 * 86400);
    }
  });
  window.addEventListener('pagehide', () => { stop(); if (dialog.open) { leaving = true; dialog.close(); } });
  window.addEventListener('pageshow', e => { if (e.persisted) stop(); });
  if (entry && !suppressed()) {
    timer = window.setTimeout(fire, delayMs);
    window.addEventListener('scroll', onScroll, { passive: true });
  }
}
